/**
 * Learning Path Editor
 * Provides path details, A4 background upload, normalized element placement, and element media editing.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Avatar, Button, Card, Col, Drawer, Empty, Form, Input, List, Modal, Popconfirm, Row, Select, Space, Spin, Statistic, Tabs, Tag, Tooltip, Upload, message } from 'antd';
import { ArrowLeftOutlined, BarChartOutlined, CopyOutlined, CustomerServiceOutlined, DeleteOutlined, DownloadOutlined, EditOutlined, EyeOutlined, FilePdfOutlined, FileTextOutlined, FormOutlined, LinkOutlined, MinusOutlined, PlusOutlined, QrcodeOutlined, ReadOutlined, SaveOutlined, ShareAltOutlined, ToolOutlined, UploadOutlined, UserOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import PagesWrapper from '../../PagesWrapper';
import RichTextEditor from '../../components/RichTextEditor';
import { AccessBanner, OwnershipManager } from '../../components/Ownership';
import { useOwnership } from '../../hooks/useOwnership';
import { getFileUrl } from '../../utils/urlUtils';
import {
  addPathElement,
  createLearningPath,
  deleteBackgroundImage,
  deleteElementMedia,
  deletePathElement,
  getLearningPathElementStatistics,
  getLearningPath,
  publishLearningPath,
  unpublishLearningPath,
  updateLearningPath,
  updatePathElement,
  uploadBackgroundImage,
  uploadElementMedia,
} from '../../services/learningPathsService';
import { getPublishedQuizLibrary } from '../../services/quizCollectionsService';
import { getPublishedSurveyLibrary } from '../../services/surveysService';
import { getPublishedTrainingProfileLibrary } from '../../services/trainingProfilesService';
import styles from './LearningPaths.module.css';

const DEFAULT_ELEMENT = {
  name: 'New Element',
  xPercent: 10,
  yPercent: 10,
  widthPercent: 20,
  heightPercent: 8,
};

const MIN_ELEMENT_SIZE = 3;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const roundCoordinate = (value) => Math.round(value * 100) / 100;

const formatElementLink = (url) => {
  if (!url) return null;

  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};

const getElementMetadata = (element) => {
  const metadata = [];

  if (element.richTextContentInfo) {
    metadata.push({
      key: 'rich-content',
      icon: <FileTextOutlined />,
      label: 'Rich content',
    });
  }

  if (element.linkUrl) {
    metadata.push({
      key: 'link',
      icon: <LinkOutlined />,
      label: formatElementLink(element.linkUrl),
    });
  }

  if (element.pdf) {
    metadata.push({
      key: 'pdf',
      icon: <FilePdfOutlined />,
      label: element.pdf.fileName,
    });
  }

  if (element.audio) {
    metadata.push({
      key: 'audio',
      icon: <CustomerServiceOutlined />,
      label: element.audio.fileName,
    });
  }

  if (element.video) {
    metadata.push({
      key: 'video',
      icon: <VideoCameraOutlined />,
      label: element.video.fileName,
    });
  }

  if (element.quizCollectionName || element.quizCollectionId) {
    metadata.push({
      key: 'quiz',
      icon: <ReadOutlined />,
      label: element.quizCollectionName || `Quiz #${element.quizCollectionId}`,
    });
  }

  if (element.surveyName || element.surveyId) {
    metadata.push({
      key: 'survey',
      icon: <FormOutlined />,
      label: element.surveyName || `Survey #${element.surveyId}`,
    });
  }

  if (element.trainingProfileName || element.trainingProfileId) {
    metadata.push({
      key: 'training-profile',
      icon: <ToolOutlined />,
      label: element.trainingProfileName || `Training Profile #${element.trainingProfileId}`,
    });
  }

  return metadata;
};

const getNextElementFrame = (interaction, deltaXPercent, deltaYPercent) => {
  const { initialFrame, mode, handle } = interaction;
  const rightEdge = initialFrame.xPercent + initialFrame.widthPercent;
  const bottomEdge = initialFrame.yPercent + initialFrame.heightPercent;

  if (mode === 'move') {
    return {
      xPercent: roundCoordinate(clamp(initialFrame.xPercent + deltaXPercent, 0, 100 - initialFrame.widthPercent)),
      yPercent: roundCoordinate(clamp(initialFrame.yPercent + deltaYPercent, 0, 100 - initialFrame.heightPercent)),
      widthPercent: initialFrame.widthPercent,
      heightPercent: initialFrame.heightPercent,
    };
  }

  let nextX = initialFrame.xPercent;
  let nextY = initialFrame.yPercent;
  let nextWidth = initialFrame.widthPercent;
  let nextHeight = initialFrame.heightPercent;

  if (handle.includes('e')) {
    nextWidth = clamp(initialFrame.widthPercent + deltaXPercent, MIN_ELEMENT_SIZE, 100 - initialFrame.xPercent);
  }

  if (handle.includes('s')) {
    nextHeight = clamp(initialFrame.heightPercent + deltaYPercent, MIN_ELEMENT_SIZE, 100 - initialFrame.yPercent);
  }

  if (handle.includes('w')) {
    nextX = clamp(initialFrame.xPercent + deltaXPercent, 0, rightEdge - MIN_ELEMENT_SIZE);
    nextWidth = clamp(rightEdge - nextX, MIN_ELEMENT_SIZE, 100 - nextX);
  }

  if (handle.includes('n')) {
    nextY = clamp(initialFrame.yPercent + deltaYPercent, 0, bottomEdge - MIN_ELEMENT_SIZE);
    nextHeight = clamp(bottomEdge - nextY, MIN_ELEMENT_SIZE, 100 - nextY);
  }

  return {
    xPercent: roundCoordinate(nextX),
    yPercent: roundCoordinate(nextY),
    widthPercent: roundCoordinate(nextWidth),
    heightPercent: roundCoordinate(nextHeight),
  };
};

/**
 * Learning Path editor page.
 * @returns {JSX.Element} Editor UI
 */
const LearningPathEditor = () => {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const pathId = isNew ? null : parseInt(id, 10);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [elementForm] = Form.useForm();
  const [path, setPath] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [ownershipOpen, setOwnershipOpen] = useState(false);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [elementStatsVisible, setElementStatsVisible] = useState(false);
  const [elementStatsLoading, setElementStatsLoading] = useState(false);
  const [elementStats, setElementStats] = useState(null);
  const [quizLibraryLoading, setQuizLibraryLoading] = useState(false);
  const [publishedQuizCollections, setPublishedQuizCollections] = useState([]);
  const [surveyLibraryLoading, setSurveyLibraryLoading] = useState(false);
  const [publishedSurveys, setPublishedSurveys] = useState([]);
  const [trainingProfileLibraryLoading, setTrainingProfileLibraryLoading] = useState(false);
  const [publishedTrainingProfiles, setPublishedTrainingProfiles] = useState([]);
  const [zoom, setZoom] = useState(1);
  const [canvasInteraction, setCanvasInteraction] = useState(null);
  const elementRichTextContent = Form.useWatch('richTextContentInfo', elementForm);
  const canvasRef = useRef(null);
  const interactionStateRef = useRef({ hasMoved: false, lastFrame: null });
  const suppressElementClickRef = useRef(false);

  const ownership = useOwnership('LearningPath', pathId || 0);
  const selectedElement = useMemo(
    () => path?.elements?.find((element) => element.id === selectedElementId) || null,
    [path, selectedElementId]
  );
  const ownerInfo = ownership.ownershipInfo?.mainOwner || ownership.ownershipInfo?.ownerInfo;
  const quizCollectionOptions = useMemo(
    () => publishedQuizCollections.map((collection) => ({
      value: collection.id,
      label: collection.name,
      collection,
    })),
    [publishedQuizCollections]
  );
  const surveyOptions = useMemo(
    () => publishedSurveys.map((survey) => ({
      value: survey.id,
      label: survey.name,
      survey,
    })),
    [publishedSurveys]
  );
  const trainingProfileOptions = useMemo(
    () => publishedTrainingProfiles.map((profile) => ({
      value: profile.id,
      label: profile.name,
      profile,
    })),
    [publishedTrainingProfiles]
  );

  const loadPath = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const detail = await getLearningPath(pathId);
      setPath(detail);
      form.setFieldsValue({
        name: detail.name,
      });
    } finally {
      setLoading(false);
    }
  }, [form, isNew, pathId]);

  const refreshPath = useCallback(async (targetId = pathId) => {
    if (!targetId) return null;
    const detail = await getLearningPath(targetId);
    setPath(detail);
    form.setFieldsValue({
      name: detail.name,
    });
    return detail;
  }, [form, pathId]);

  useEffect(() => {
    loadPath();
  }, [loadPath]);

  useEffect(() => {
    if (selectedElement) {
      elementForm.setFieldsValue(selectedElement);
    }
  }, [elementForm, selectedElement]);

  const loadAssignmentLibraries = useCallback(async () => {
    setQuizLibraryLoading(true);
    setSurveyLibraryLoading(true);
    setTrainingProfileLibraryLoading(true);
    try {
      const [quizResult, surveyResult, trainingProfileResult] = await Promise.all([
        getPublishedQuizLibrary('', { suppressNotification: true }),
        getPublishedSurveyLibrary('', { suppressNotification: true }),
        getPublishedTrainingProfileLibrary({ suppressNotification: true }),
      ]);

      setPublishedQuizCollections(quizResult || []);
      setPublishedSurveys(surveyResult || []);
      setPublishedTrainingProfiles(trainingProfileResult || []);
    } finally {
      setQuizLibraryLoading(false);
      setSurveyLibraryLoading(false);
      setTrainingProfileLibraryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!pathId) return;
    loadAssignmentLibraries();
  }, [loadAssignmentLibraries, pathId]);

  const applyLocalElementUpdate = useCallback((elementId, frame) => {
    setPath((current) => {
      if (!current) return current;

      return {
        ...current,
        elements: current.elements.map((element) => (
          element.id === elementId ? { ...element, ...frame } : element
        )),
      };
    });
  }, []);

  const persistElementFrame = useCallback(async (elementId, frame) => {
    try {
      const updated = await updatePathElement(elementId, frame);
      setPath((current) => {
        if (!current) return current;

        return {
          ...current,
          elements: current.elements.map((element) => (
            element.id === updated.id ? updated : element
          )),
        };
      });
    } catch {
      await refreshPath();
    }
  }, [pathId, refreshPath]);

  useEffect(() => {
    if (!canvasInteraction) return undefined;

    const handlePointerMove = (event) => {
      const canvasBounds = canvasRef.current?.getBoundingClientRect();
      if (!canvasBounds) return;

      const deltaXPercent = ((event.clientX - canvasInteraction.startClientX) / canvasBounds.width) * 100;
      const deltaYPercent = ((event.clientY - canvasInteraction.startClientY) / canvasBounds.height) * 100;
      const nextFrame = getNextElementFrame(canvasInteraction, deltaXPercent, deltaYPercent);

      interactionStateRef.current = {
        hasMoved: true,
        lastFrame: nextFrame,
      };

      applyLocalElementUpdate(canvasInteraction.elementId, nextFrame);
    };

    const handlePointerUp = () => {
      const { hasMoved, lastFrame } = interactionStateRef.current;

      if (hasMoved && lastFrame) {
        suppressElementClickRef.current = true;
        persistElementFrame(canvasInteraction.elementId, lastFrame);
      }

      interactionStateRef.current = { hasMoved: false, lastFrame: null };
      setCanvasInteraction(null);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [applyLocalElementUpdate, canvasInteraction, persistElementFrame]);

  const ensurePathExists = async () => {
    const values = await form.validateFields(['name']);
    if (!isNew && pathId) return pathId;

    const created = await createLearningPath(values, { successMessage: 'Learning path created' });
    navigate(`/learning-paths/${created.id}`, { replace: true });
    return created.id;
  };

  const handleSaveDetails = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (isNew) {
        const created = await createLearningPath(values, { successMessage: 'Learning path created' });
        navigate(`/learning-paths/${created.id}`, { replace: true });
      } else {
        const updated = await updateLearningPath(pathId, values, { successMessage: 'Learning path saved' });
        setPath(updated);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBackgroundUpload = async ({ file }) => {
    const targetId = await ensurePathExists();
    await uploadBackgroundImage(targetId, file, {}, { successMessage: 'Background image uploaded' });
    await refreshPath(targetId);
  };

  const handleAddElement = async () => {
    const targetId = await ensurePathExists();
    const created = await addPathElement(targetId, DEFAULT_ELEMENT, { successMessage: 'Element added' });
    await refreshPath(targetId);
    setSelectedElementId(created.id);
    setDrawerOpen(true);
  };

  const handleSelectElement = (element) => {
    setSelectedElementId(element.id);
    setDrawerOpen(true);
  };

  const handleShowElementStatistics = async (element) => {
    setElementStatsVisible(true);
    setElementStatsLoading(true);
    try {
      const stats = await getLearningPathElementStatistics(element.id, { suppressNotification: true });
      setElementStats(stats);
    } finally {
      setElementStatsLoading(false);
    }
  };

  const handleDeleteElement = async (elementId, event) => {
    event?.stopPropagation();
    await deletePathElement(elementId, { successMessage: 'Element deleted' });

    setPath((current) => {
      if (!current) return current;

      return {
        ...current,
        elements: current.elements.filter((element) => element.id !== elementId),
      };
    });

    if (selectedElementId === elementId) {
      setDrawerOpen(false);
      setSelectedElementId(null);
    }
  };

  const startElementInteraction = (event, element, mode, handle = null) => {
    if (!canvasRef.current) return;

    event.preventDefault();
    event.stopPropagation();

    setSelectedElementId(element.id);
    interactionStateRef.current = { hasMoved: false, lastFrame: null };
    setCanvasInteraction({
      elementId: element.id,
      mode,
      handle,
      startClientX: event.clientX,
      startClientY: event.clientY,
      initialFrame: {
        xPercent: element.xPercent,
        yPercent: element.yPercent,
        widthPercent: element.widthPercent,
        heightPercent: element.heightPercent,
      },
    });
  };

  const handleSaveElement = async () => {
    const values = await elementForm.validateFields();
    const updated = await updatePathElement(selectedElement.id, {
      ...values,
      clearQuizCollection: !values.quizCollectionId && Boolean(selectedElement.quizCollectionId),
      clearSurvey: !values.surveyId && Boolean(selectedElement.surveyId),
      clearTrainingProfile: !values.trainingProfileId && Boolean(selectedElement.trainingProfileId),
    }, { successMessage: 'Element saved' });
    setPath((current) => ({
      ...current,
      elements: current.elements.map((element) => (element.id === updated.id ? updated : element)),
    }));
    setDrawerOpen(false);
  };

  const handleMediaUpload = async (mediaType, file) => {
    await uploadElementMedia(selectedElement.id, mediaType, file, {}, { successMessage: `${mediaType.toUpperCase()} uploaded` });
    await refreshPath();
  };

  const handlePublishToggle = async () => {
    if (!path) return;
    if (path.isPublished) {
      await unpublishLearningPath(path.id, { successMessage: 'Learning path unpublished' });
    } else {
      await publishLearningPath(path.id, { successMessage: 'Learning path published' });
    }
    setPath((current) => (current ? { ...current, isPublished: !current.isPublished } : current));
  };

  const copyPublicLink = async () => {
    await navigator.clipboard.writeText(`${window.location.origin}/learning-path/${path.id}`);
    message.success('Public link copied');
  };

  const handleDownloadQrCode = () => {
    const svg = document.getElementById('learning-path-editor-qr-code');
    if (!svg) {
      return;
    }

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `${path?.name || 'learning-path'}-qrcode.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    };

    img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
  };

  if (loading) {
    return (
      <PagesWrapper>
        <Spin />
      </PagesWrapper>
    );
  }

  return (
    <PagesWrapper>
      <div className={styles.editorContainer}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/learning-paths')}>
              Back
            </Button>
            <h1>{isNew ? 'Create Learning Path' : (path?.name || 'Untitled Learning Path')}</h1>
            {path && <Tag color={path.isPublished ? 'green' : 'default'}>{path.isPublished ? 'Published' : 'Draft'}</Tag>}
            {path?.addedByName && (
              <Tooltip title="Created by">
                <Space size="small" className={styles.createdBy}>
                  <Avatar size="small" icon={<UserOutlined />} />
                  <span>{path.addedByName}</span>
                </Space>
              </Tooltip>
            )}
          </div>
          <Space className={styles.headerActions}>
            {path && (
              <Tooltip title="Open public page">
                <Button icon={<EyeOutlined />} disabled={!path.isPublished} onClick={() => window.open(`/learning-path/${path.id}`, '_blank')}>
                  Open
                </Button>
              </Tooltip>
            )}
            {path && (
              <Tooltip title="Statistics">
                <Button icon={<BarChartOutlined />} onClick={() => navigate(`/learning-paths/${path.id}/statistics`)}>
                  Statistics
                </Button>
              </Tooltip>
            )}
            {path && (
              <Tooltip title="Copy public link">
                <Button icon={<CopyOutlined />} disabled={!path.isPublished} onClick={copyPublicLink}>
                  Copy Link
                </Button>
              </Tooltip>
            )}
            {path && (
              <Tooltip title="QR code">
                <Button icon={<QrcodeOutlined />} disabled={!path.isPublished} onClick={() => setQrModalVisible(true)}>
                  QR Code
                </Button>
              </Tooltip>
            )}
            {path && ownership.canManageOwnership && (
              <Tooltip title="Share">
                <Button icon={<ShareAltOutlined />} onClick={() => setOwnershipOpen(true)}>
                  Share
                </Button>
              </Tooltip>
            )}
            {path && <Button onClick={handlePublishToggle}>{path.isPublished ? 'Unpublish' : 'Publish'}</Button>}
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSaveDetails}>Save</Button>
          </Space>
        </div>

        {path && ownership.ownershipInfo && (
          <AccessBanner
            accessLevel={ownership.ownershipInfo.accessLevel}
            ownerInfo={ownerInfo}
            canEdit={ownership.ownershipInfo.canEdit}
            canDelete={ownership.ownershipInfo.canDelete}
          />
        )}

        <Tabs
          items={[
            {
              key: 'details',
              label: 'Details',
              children: (
                <Card>
                  <Form form={form} layout="vertical">
                    <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
                      <Input maxLength={255} />
                    </Form.Item>
                  </Form>
                </Card>
              ),
            },
            {
              key: 'canvas',
              label: 'A4 Canvas',
              children: (
                <div className={styles.editorLayout}>
                  <div className={styles.canvasShell}>
                    <div className={styles.canvasToolbar}>
                      <Space wrap>
                        <Upload beforeUpload={(file) => { handleBackgroundUpload({ file }).catch(() => {}); return false; }} showUploadList={false} accept="image/*">
                          <Button className={styles.canvasToolbarButton} icon={<UploadOutlined />}>{path?.backgroundImage ? 'Replace A4 Image' : 'Upload A4 Image'}</Button>
                        </Upload>
                        {path?.backgroundImage && (
                          <Popconfirm title="Remove background image?" onConfirm={async () => {
                            await deleteBackgroundImage(path.id);
                            setPath((current) => (current ? { ...current, backgroundImage: null } : current));
                          }}>
                            <Button className={styles.canvasToolbarButton} danger>Remove Image</Button>
                          </Popconfirm>
                        )}
                        <Button className={styles.canvasToolbarButton} icon={<PlusOutlined />} onClick={handleAddElement}>Add Element</Button>
                      </Space>
                      <Space className={styles.canvasZoomControls}>
                        <Button className={styles.canvasToolbarButton} icon={<MinusOutlined />} onClick={() => setZoom((value) => Math.max(0.5, value - 0.1))} />
                        <span className={styles.zoomLabel}>{Math.round(zoom * 100)}%</span>
                        <Button className={styles.canvasToolbarButton} icon={<PlusOutlined />} onClick={() => setZoom((value) => Math.min(2, value + 0.1))} />
                      </Space>
                    </div>

                    <div ref={canvasRef} className={styles.a4Page} style={{ transform: `scale(${zoom})`, marginBottom: `${(zoom - 1) * 900}px` }}>
                      {path?.backgroundImage ? (
                        <img className={styles.backgroundImage} src={getFileUrl(path.backgroundImage.relativeUrl)} alt={path.backgroundImage.altText || path.name} />
                      ) : (
                        <div className={styles.backgroundPlaceholder}>Upload an A4 background image to position elements.</div>
                      )}
                      {(path?.elements || []).map((element) => (
                        <button
                          key={element.id}
                          type="button"
                          className={`${styles.pathElement} ${element.id === selectedElementId ? styles.pathElementSelected : ''}`}
                          style={{
                            left: `${element.xPercent}%`,
                            top: `${element.yPercent}%`,
                            width: `${element.widthPercent}%`,
                            height: `${element.heightPercent}%`,
                          }}
                          onPointerDown={(event) => startElementInteraction(event, element, 'move')}
                          onClick={() => {
                            if (suppressElementClickRef.current) {
                              suppressElementClickRef.current = false;
                              return;
                            }

                            handleSelectElement(element);
                          }}
                        >
                          <span className={styles.pathElementLabel}>{element.name}</span>
                          {['nw', 'ne', 'sw', 'se'].map((handle) => (
                            <span
                              key={`${element.id}-${handle}`}
                              className={`${styles.resizeHandle} ${styles[`resizeHandle${handle.toUpperCase()}`]}`}
                              onPointerDown={(event) => startElementInteraction(event, element, 'resize', handle)}
                            />
                          ))}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className={styles.sidePanel}>
                    <Card title="Elements">
                      {path?.elements?.length ? (
                        <List
                          className={styles.elementList}
                          dataSource={[...path.elements].sort((a, b) => a.orderIndex - b.orderIndex)}
                          renderItem={(element) => (
                            <List.Item className={`${styles.elementListItem} ${element.id === selectedElementId ? styles.elementListItemActive : ''}`}>
                              <div className={styles.elementCard}>
                                <div className={styles.elementCardHeader}>
                                  <div className={styles.elementTitleGroup}>
                                    <span className={styles.elementTitle}>{element.name}</span>
                                    <span className={styles.elementSubtitle}>Use the canvas to move and resize. Use the actions to edit content or inspect clicks.</span>
                                  </div>
                                  <Space size="small" className={styles.elementCardActions}>
                                    <Tooltip title="Edit element">
                                      <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleSelectElement(element)} />
                                    </Tooltip>
                                    <Tooltip title="Element statistics">
                                      <Button type="text" size="small" icon={<BarChartOutlined />} onClick={() => handleShowElementStatistics(element)} />
                                    </Tooltip>
                                    <Popconfirm
                                      title="Delete element?"
                                      onConfirm={() => handleDeleteElement(element.id)}
                                    >
                                      <Button
                                        type="text"
                                        danger
                                        size="small"
                                        icon={<DeleteOutlined />}
                                        onClick={(event) => event.stopPropagation()}
                                      />
                                    </Popconfirm>
                                  </Space>
                                </div>
                                {getElementMetadata(element).length ? (
                                  <div className={styles.elementMetaList}>
                                    {getElementMetadata(element).map((item) => (
                                      <span key={`${element.id}-${item.key}`} className={styles.elementMetaChip}>
                                        <span className={styles.elementMetaIcon}>{item.icon}</span>
                                        <span className={styles.elementMetaLabel}>{item.label}</span>
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <div className={styles.elementMetaEmpty}>No linked content yet</div>
                                )}
                              </div>
                            </List.Item>
                          )}
                        />
                      ) : <Empty description="No elements yet" />}
                    </Card>
                  </div>
                </div>
              ),
            },
          ]}
        />
      </div>

      <Drawer
        title={selectedElement ? `Edit: ${selectedElement.name}` : 'Edit Element'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={720}
        extra={<Button type="primary" onClick={handleSaveElement}>Save Element</Button>}
      >
        {selectedElement && (
          <Form form={elementForm} layout="vertical">
            <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Element name is required' }]}>
              <Input maxLength={255} />
            </Form.Item>
            <Form.Item name="richTextContentInfo" label="Rich Content">
              <RichTextEditor
                value={elementRichTextContent || ''}
                onChange={(html) => elementForm.setFieldValue('richTextContentInfo', html)}
                parentType="LearningPath"
                parentId={pathId}
                maxLength={5000}
                height={180}
                minHeight={120}
                maxHeight={320}
              />
            </Form.Item>
            <Form.Item name="linkUrl" label="External Link">
              <Input placeholder="https://example.com" />
            </Form.Item>
            <Form.Item name="quizCollectionId" label="Published Quiz Collection">
              <Select
                allowClear
                showSearch
                loading={quizLibraryLoading}
                placeholder="Choose a published quiz collection"
                options={quizCollectionOptions}
                optionFilterProp="label"
                filterOption={(input, option) => {
                  const label = option?.label?.toLowerCase?.() || '';
                  const owner = option?.collection?.ownerName?.toLowerCase?.() || '';
                  return label.includes(input.toLowerCase()) || owner.includes(input.toLowerCase());
                }}
                optionRender={(option) => {
                  const collection = option.data.collection;
                  return (
                    <div className={styles.quizOption}>
                      <div className={styles.quizOptionName}>{collection.name}</div>
                      <div className={styles.quizOptionMeta}>
                        <span>{collection.publishedQuestionCount ?? collection.questionCount ?? 0} questions</span>
                        <span>{collection.topicCount ?? 0} topics</span>
                        <span>{collection.ownerName || 'Published quiz'}</span>
                      </div>
                    </div>
                  );
                }}
              />
            </Form.Item>
            <Form.Item name="surveyId" label="Published Survey">
              <Select
                allowClear
                showSearch
                loading={surveyLibraryLoading}
                placeholder="Choose a published survey"
                options={surveyOptions}
                optionFilterProp="label"
                filterOption={(input, option) => {
                  const label = option?.label?.toLowerCase?.() || '';
                  const owner = option?.survey?.ownerName?.toLowerCase?.() || '';
                  return label.includes(input.toLowerCase()) || owner.includes(input.toLowerCase());
                }}
                optionRender={(option) => {
                  const survey = option.data.survey;
                  return (
                    <div className={styles.quizOption}>
                      <div className={styles.quizOptionName}>{survey.name}</div>
                      <div className={styles.quizOptionMeta}>
                        <span>{survey.questionCount ?? 0} questions</span>
                        <span>{survey.responseCount ?? 0} responses</span>
                        <span>{survey.ownerName || 'Published survey'}</span>
                      </div>
                    </div>
                  );
                }}
              />
            </Form.Item>
            <Form.Item name="trainingProfileId" label="Published Training Profile">
              <Select
                allowClear
                showSearch
                loading={trainingProfileLibraryLoading}
                placeholder="Choose a published training profile"
                options={trainingProfileOptions}
                optionFilterProp="label"
                filterOption={(input, option) => {
                  const label = option?.label?.toLowerCase?.() || '';
                  const country = option?.profile?.countryName?.toLowerCase?.() || '';
                  const owner = option?.profile?.ownerName?.toLowerCase?.() || '';
                  return label.includes(input.toLowerCase()) || country.includes(input.toLowerCase()) || owner.includes(input.toLowerCase());
                }}
                optionRender={(option) => {
                  const profile = option.data.profile;
                  return (
                    <div className={styles.quizOption}>
                      <div className={styles.quizOptionName}>{profile.name}</div>
                      <div className={styles.quizOptionMeta}>
                        <span>{profile.countryName || 'No country'}</span>
                        <span>{profile.quizCollectionCount ?? 0} quizzes</span>
                        <span>{profile.ownerName || 'Published profile'}</span>
                      </div>
                    </div>
                  );
                }}
              />
            </Form.Item>
            <div className={styles.mediaStack}>
              {['pdf', 'audio', 'video'].map((mediaType) => (
                <div key={mediaType} className={styles.mediaRow}>
                  <div className={styles.mediaRowText}>
                    <div className={styles.mediaRowTitle}>{mediaType.toUpperCase()}</div>
                    <div className={styles.mediaRowSubtitle}>
                      {selectedElement[mediaType]?.fileName || `No ${mediaType} file attached`}
                    </div>
                  </div>
                  <Space>
                    {selectedElement[mediaType] ? (
                      <>
                        <Button size="small" href={`/api/files/${selectedElement[mediaType].id}/download`} target="_blank" rel="noreferrer">
                          Open
                        </Button>
                        <Button
                          size="small"
                          danger
                          onClick={async () => {
                            await deleteElementMedia(selectedElement.id, mediaType);
                            setPath((current) => {
                              if (!current) return current;

                              return {
                                ...current,
                                elements: current.elements.map((element) => (
                                  element.id === selectedElement.id ? { ...element, [mediaType]: null } : element
                                )),
                              };
                            });
                          }}
                        >
                          Remove
                        </Button>
                      </>
                    ) : (
                      <Upload beforeUpload={(file) => { handleMediaUpload(mediaType, file).catch(() => {}); return false; }} showUploadList={false}>
                        <Button size="small" icon={<UploadOutlined />}>Upload</Button>
                      </Upload>
                    )}
                  </Space>
                </div>
              ))}
            </div>
            <Popconfirm
              title="Delete element?"
              onConfirm={async () => {
                await handleDeleteElement(selectedElement.id);
              }}
            >
              <Button danger icon={<DeleteOutlined />}>Delete Element</Button>
            </Popconfirm>
          </Form>
        )}
      </Drawer>

      {path && (
        <Modal
          title="QR Code"
          open={qrModalVisible}
          onCancel={() => setQrModalVisible(false)}
          centered
          footer={[
            <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={handleDownloadQrCode}>
              Download QR Code
            </Button>,
            <Button key="close" onClick={() => setQrModalVisible(false)}>
              Close
            </Button>,
          ]}
        >
          <div className={styles.qrCodeContainer}>
            <QRCodeSVG
              id="learning-path-editor-qr-code"
              value={`${window.location.origin}/learning-path/${path.id}`}
              size={256}
              level="H"
              includeMargin
            />
            <p className={styles.qrCodeCollectionName}>{path.name}</p>
            <p className={styles.qrCodeLink}>{`${window.location.origin}/learning-path/${path.id}`}</p>
          </div>
        </Modal>
      )}

      <Modal
        title={elementStats?.elementName ? `Statistics: ${elementStats.elementName}` : 'Element statistics'}
        open={elementStatsVisible}
        onCancel={() => {
          setElementStatsVisible(false);
          setElementStats(null);
        }}
        footer={null}
        width={760}
      >
        {elementStatsLoading ? <Spin /> : elementStats ? (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div className={styles.elementStatsHeader}>
              <div className={styles.elementStatsTitle}>{elementStats.elementName}</div>
              <div className={styles.elementStatsSubtitle}>{elementStats.learningPathName}</div>
            </div>
            <Row gutter={[12, 12]}>
              <Col xs={12} md={8}><Card size="small"><Statistic title="Clicks" value={elementStats.elementClicks} /></Card></Col>
              <Col xs={12} md={8}><Card size="small"><Statistic title="PDF" value={elementStats.pdfOpens} /></Card></Col>
              <Col xs={12} md={8}><Card size="small"><Statistic title="Audio" value={elementStats.audioOpens} /></Card></Col>
              <Col xs={12} md={8}><Card size="small"><Statistic title="Video" value={elementStats.videoOpens} /></Card></Col>
              <Col xs={12} md={8}><Card size="small"><Statistic title="Links" value={elementStats.linkOpens} /></Card></Col>
              <Col xs={12} md={8}><Card size="small"><Statistic title="Quizzes" value={elementStats.quizOpens} /></Card></Col>
              <Col xs={12} md={8}><Card size="small"><Statistic title="Surveys" value={elementStats.surveyOpens} /></Card></Col>
              <Col xs={12} md={8}><Card size="small"><Statistic title="Training Profiles" value={elementStats.trainingProfileOpens} /></Card></Col>
            </Row>
            <Card size="small">
              <Statistic title="Total element interactions" value={elementStats.totalInteractions} />
            </Card>
          </Space>
        ) : <Empty description="No statistics found" />}
      </Modal>

      {path && (
        <OwnershipManager
          open={ownershipOpen}
          onClose={() => setOwnershipOpen(false)}
          ownershipInfo={ownership.ownershipInfo}
          onShare={ownership.shareWith}
          onRevoke={ownership.revoke}
          onGetAvailableUsers={ownership.getAvailableUsers}
          availableUsers={ownership.availableUsers}
          loading={ownership.loading}
          loadingAvailableUsers={ownership.loadingAvailableUsers}
          canManageOwnership={ownership.canManageOwnership}
        />
      )}
    </PagesWrapper>
  );
};

export default LearningPathEditor;
